import { DocumentFile } from '@api/types';
import { LoadingSkeleton, SmartLink } from '@components';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadForOfflineOutlined from '@mui/icons-material/DownloadForOfflineOutlined';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Box, styled } from '@mui/material';
import { colors } from '@themes/colors';
import { breakpoints } from '@themes/theme.helpers';

const DocumentCardBox = styled(Box)`
  background: #fff;
  border-radius: 4px;

  .link-text {
    display: flex;
    width: 100%;
    flex-direction: row;
    gap: 8px;
    padding: 16px;

    ${breakpoints.up('md')} {
      gap: 16px;
      align-items: center;
      justify-content: flex-start;
      flex-direction: row;
    }
  }
`;

const IconBox = styled(Box)`
  display: none;
  ${breakpoints.up('sm')} {
    display: inline-block;
    width: 36px;
    height: 36px;
  }
`;

const DocumentIcon = styled(DescriptionOutlinedIcon)`
  color: ${colors.text.tertiary};
  font-size: 36px;
`;

const DownloadIcon = styled(DownloadForOfflineOutlined)`
  font-size: 36px;
`;

const RemoveIcon = styled(HighlightOffIcon)`
  font-size: 36px;
  :hover {
    cursor: pointer;
    color: ${colors.error.main};
  }
`;

const DataBox = styled(Box)`
  flex-grow: 1;
  display: flex;
  flex-direction: row;
  gap: 4px;
  word-break: break-word;
  ${breakpoints.up('md')} {
    flex-direction: column;
    gap: 4px;
  }
`;

const Size = styled(Box)`
  color: ${colors.text.tertiary};
`;
const TitleBox = styled(Box)`
  color: ${colors.text.primary};
  font-weight: 700;
`;

export type DocumentCardProps = DocumentFile & {
  isWorking?: boolean;
  onRemove?: () => void;
};

export const DocumentCard = ({
  isWorking,
  name,
  displayName,
  fileSize,
  url,
  altText = '',
  onRemove,
}: DocumentCardProps) => {
  const content = (
    <>
      <IconBox>{isWorking ? <LoadingSkeleton /> : <DocumentIcon />}</IconBox>
      <DataBox>
        <TitleBox>{isWorking ? <LoadingSkeleton /> : <>{displayName || name}</>}</TitleBox>
        <Size>{isWorking ? <LoadingSkeleton /> : fileSize}</Size>
      </DataBox>
      {onRemove ? (
        <IconBox onClick={onRemove}>
          <RemoveIcon sx={{ fontSize: '36px' }} />
        </IconBox>
      ) : (
        <IconBox>{isWorking ? <LoadingSkeleton /> : <DownloadIcon />}</IconBox>
      )}
    </>
  );

  return (
    <DocumentCardBox className='hover-shadow' title={altText} sx={{ position: 'relative' }}>
      {onRemove ? (
        <Box className='link-text'>{content}</Box>
      ) : (
        <SmartLink href={url} className='link' target='_blank' download>
          {content}
        </SmartLink>
      )}
    </DocumentCardBox>
  );
};
